import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../services/knowledge-api';
import { defaultErrorMessage } from '../../../constants/errorMessages';

import { useClickOutside } from '../../../hooks';
import { useDocsPageContext } from '../../../hooks/useDocsPageContext';
import { useEscapeKeyListener } from '../../../hooks/useEscapeKeyListener';
import { useResponsiveBreakpoints } from '../../../hooks/useResponsiveBreakpoints';
import { useFetchOptionsForPaginatedSelect } from '../../../hooks/useFetchOptionsForPaginatedSelect';

import { CONTENT_TYPE } from '../../../constants/docs';
import {
    updateBreadcrumbs,
    moveItemsAPIRequest,
    mutateCashedDataAfterMoveAction,
    removeMovedItemsFromSelectedItemsState,
    getFoldersDataFromPromiseAllSettledResponse,
    getMovedItemsIdsFromPromiseAllSettledResponse,
} from '../../../helpers/docsUtils';
import { updateSidePanelStateAfterMovingItems } from '../../../helpers/docsSidePanelUtils';

import ModalHeader from '../../../design-system/ModalHeader/ModalHeader';
import MobilePopup from '../../../design-system/MobilePopup/MobilePopup';
import InfiniteScrollList from '../../../components/InfiniteScrollList/InfiniteScrollList';
import ClientSideFilteredSearchBar from '../../../components/ClientSideFilteredSearchBar/ClientSideFilteredSearchBar';
import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import MoveToFolderItem from '../MoveToFolderItem/MoveToFolderItem';
import Modal from '../../../design-system/Modal/Modal';

export const ROOT_FOLDER_OPTION = {
    id: 'root-folder',
    label: 'Docs',
    location: [],
    isRootFolder: true,
};

const MoveToPanel = ({
    itemsToMove,
    clearAllSelectedItems,
    requestKeysToMutate,
    currentRequestKeyToMutate,
    viewType = 'modal', // 'modal' | 'popup'
    movedItemsParentLocation: _movedItemsParentLocation = null, // if null the current page location is used as moved Items Parent Location
    setSelectedItems,
    onClose,
}) => {
    const panelRef = useRef();

    useClickOutside(panelRef, handleClose);
    useEscapeKeyListener(onClose);

    const [loadingFolderId, setLoadingFolderId] = useState(null);
    const [errorAlert, setErrorAlert] = useState(null);

    const {
        options: folders,
        optionsLoading,
        canLoadMoreOptions,
        isFirstRequestCompleted,
        setPage,
    } = useFetchOptionsForPaginatedSelect({
        client,
        route: API.ROUTES.knowledge.folder,
        formatResponseToOptions: (results) =>
            results.map(({ id, label, location }) => ({ id, label, location })),
    });

    const foldersWithRootFolder = useMemo(() => {
        const foldersToMoveIds = Object.values(itemsToMove || {})
            .filter(({ content_type }) => content_type === CONTENT_TYPE.folder)
            .map(({ id }) => id);
        const foldersOptionsWithoutFoldersToMove =
            folders?.filter(({ id }) => !foldersToMoveIds.includes(id)) || [];
        return [{ ...ROOT_FOLDER_OPTION }, ...foldersOptionsWithoutFoldersToMove];
    }, [folders, itemsToMove]);
    const [filteredFolders, setFilteredFolders] = useState(folders);

    const {
        location: currentPageLocation,
        setSidePanelFolders,
        setExpandedFolders,
        setLocation,
    } = useDocsPageContext() || {};

    const movedItemsParentLocation = _movedItemsParentLocation || currentPageLocation || [];

    const handleMove = async (targetFolder) => {
        if (!itemsToMove) return;

        try {
            // on [] location there are the root folder and search results
            const isSameFolder =
                !!movedItemsParentLocation?.length &&
                JSON.stringify(targetFolder.location) === JSON.stringify(movedItemsParentLocation);

            if (isSameFolder) {
                onClose();
                return;
            }

            setLoadingFolderId(targetFolder.id);
            const itemsToMoveArray = Object.values(itemsToMove) || [];

            const data = await moveItemsAPIRequest({
                targetFolder,
                itemsToMove: itemsToMoveArray,
                setErrorAlert,
            });

            await mutateCashedDataAfterMoveAction({
                requestKeysToMutate,
                currentRequestKeyToMutate,
                movedItemsParentLocation,
                targetFolderLocation: targetFolder.location,
            });

            const movedFolders = getFoldersDataFromPromiseAllSettledResponse({
                data,
                selectedItemsArray: itemsToMoveArray,
            });

            const isSearchApplied =
                currentRequestKeyToMutate && currentRequestKeyToMutate.includes('query=');

            await updateSidePanelStateAfterMovingItems({
                targetFolder,
                movedFolders,
                isSearchApplied,
                movedItemsParentLocation,
                setExpandedFolders,
                setSidePanelFolders,
            });

            updateBreadcrumbs({ movedFolders, setLocation });

            if (clearAllSelectedItems) {
                clearAllSelectedItems();
            } else {
                const movedItemsIds = getMovedItemsIdsFromPromiseAllSettledResponse({ data });
                removeMovedItemsFromSelectedItemsState({ movedItemsIds, setSelectedItems });
            }

            onClose();
        } catch (e) {
            setLoadingFolderId(null);
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    const { isMobile } = useResponsiveBreakpoints();

    function handleClose(e) {
        e?.stopPropagation();
        e?.preventDefault();
        onClose();
    }

    const topFixedContent = (
        <div className="flex flex-col">
            <ModalHeader
                onClose={onClose}
                title="Move To"
                outlined
                withEscapeButton={!isMobile}
                titleStyles="font-heading-bold text-heading-bold-s text-neutral-500"
                customPaddings="py-3 px-5"
            />

            <div className="pt-5 px-5">
                <ClientSideFilteredSearchBar
                    size="xs"
                    list={foldersWithRootFolder}
                    setFilteredList={setFilteredFolders}
                    keyToFilter={['label']}
                />
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </div>
    );

    const foldersListClassName = classNames('flex-1 pt-4 mt-2 px-5 overflow-y-auto', {
        'md:pb-3': !!filteredFolders?.length,
        'min-h-[38px]': !filteredFolders?.length && !optionsLoading,
        'min-h-[110px]': !isFirstRequestCompleted && optionsLoading,
        'min-h-[70px]': !!isFirstRequestCompleted && optionsLoading,
    });

    const foldersList = (
        <div className={foldersListClassName}>
            <InfiniteScrollList
                handleFetch={() => setPage((page) => page + 1)}
                canLoadMore={canLoadMoreOptions}
                items={filteredFolders}
                loading={optionsLoading}
                showLoader={!loadingFolderId}
                gap={0}
            >
                <div className="flex flex-col gap-1">
                    {filteredFolders?.map((folder) => {
                        const { id } = folder;
                        const isLoading = loadingFolderId === id;

                        return (
                            <MoveToFolderItem
                                key={id}
                                folder={folder}
                                isLoading={isLoading}
                                isDisabled={!!loadingFolderId}
                                handleMove={() => handleMove(folder)}
                            />
                        );
                    })}
                </div>
            </InfiniteScrollList>
        </div>
    );

    if (isMobile) {
        return (
            <MobilePopup
                onClose={onClose}
                closeButtonText="Cancel"
                minHeight="90vh"
                maxHeight="90vh"
                containerCustomPaddings="pb-4"
                contentCustomPaddings="pt-0"
                bottomButtonsCustomPaddings="px-5 pt-3"
                topFixedContent={topFixedContent}
                customGap={8}
            >
                <div className="flex flex-col bg-white">{foldersList}</div>
            </MobilePopup>
        );
    }

    if (viewType === 'popup') {
        return (
            <div className="absolute top-[36px] right-0 z-50">
                <div
                    ref={panelRef}
                    className="w-[548px] flex flex-col bg-white rounded-2 border-1 border-neutral-200 shadow-l1 overflow-y-auto max-h-[416px]"
                >
                    {topFixedContent}
                    {foldersList}
                </div>
            </div>
        );
    }

    return (
        <Modal size="semiMedium" onClose={onClose} resetPadding>
            <div className="flex flex-col overflow-y-auto max-h-[369px]">
                {topFixedContent}
                {foldersList}
            </div>
        </Modal>
    );
};

export default MoveToPanel;
